import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  ClipboardList,
  FileText,
  Loader2,
  Sparkles,
  Target,
} from "lucide-react";
import { fetchFreeLessonByToken } from "../lib/freeLessonApi";
import { track } from "../lib/analytics";
import { AtomLoader, GENERATOR_LOADER_MESSAGES } from "../components/AtomLoader";

/**
 * /my-lesson/:token — magic-link landing page. Polls the API until the
 * trimmed lesson is ready, then renders the 6 sections + persistent
 * upgrade bar. The token itself doubles as an auth credential — the
 * backend drops a session cookie when this page loads, so subsequent
 * navigation (Dashboard, Library, etc.) treats the user as signed-in.
 */
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90_000;

export default function MyLessonPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let started = Date.now();

    async function poll() {
      try {
        const data = await fetchFreeLessonByToken(token);
        if (cancelled) return;
        setElapsed(Date.now() - started);
        if (data.status === "ready" && data.lesson) {
          setState({ status: "ready", data });
          track("free_lesson_viewed", {
            grade: data.grade,
            subject: data.subject,
            transferable_skill: data.transferable_skill,
          });
          return;
        }
        if (data.status === "failed") {
          setState({ status: "failed" });
          return;
        }
        if (Date.now() - started > POLL_TIMEOUT_MS) {
          setState({ status: "timeout" });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setState({ status: "not_found" });
        } else {
          setState({ status: "failed" });
        }
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#f4f5f7] font-montserrat">
      <header className="px-6 py-4 sm:px-8 sm:py-5 border-b border-[#4a6984]/10 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            data-testid="my-lesson-home"
            className="inline-flex items-center gap-2 font-extrabold text-[#1c2d3d] tracking-tight"
          >
            <span className="h-8 w-8 rounded-lg bg-[#96ded1] grid place-items-center font-black text-[#1c2d3d]">
              A
            </span>
            <span className="text-lg">Atomic Edu</span>
          </Link>
          <Link
            to="/pricing"
            data-testid="my-lesson-plans"
            className="text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
          >
            Plans →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {state.status === "loading" && (
          <LoadingState elapsed={elapsed} />
        )}
        {state.status === "ready" && (
          <LessonView data={state.data} />
        )}
        {state.status === "failed" && (
          <ErrorState
            title="Something went wrong"
            body="We hit a snag generating your lesson. Try the generator again from the homepage and we'll send you a fresh link."
          />
        )}
        {state.status === "timeout" && (
          <ErrorState
            title="Still cooking…"
            body="Generation is taking longer than expected. Refresh in a minute — your lesson will be waiting."
          />
        )}
        {state.status === "not_found" && (
          <ErrorState
            title="Link not found"
            body="This lesson link couldn't be found. It may have expired — head back to the homepage to generate a new one."
          />
        )}
      </main>

      <UpgradeBar status={state.status} />
    </div>
  );
}

const LoadingState = ({ elapsed }) => (
  <div
    data-testid="my-lesson-loading"
    className="flex items-center justify-center py-8"
  >
    <AtomLoader
      messages={GENERATOR_LOADER_MESSAGES}
      sublabel={
        elapsed > 15000
          ? "Complex prompts sometimes take up to a minute."
          : undefined
      }
    />
  </div>
);

const ErrorState = ({ title, body }) => (
  <div
    data-testid="my-lesson-error"
    className="ae-card rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm p-8 sm:p-10 text-center space-y-4"
  >
    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#fde68a]">
      <AlertCircle className="h-5 w-5 text-[#92400e]" />
    </div>
    <h2 className="font-black text-2xl text-[#1c2d3d] tracking-tight">{title}</h2>
    <p className="text-sm text-[#4a6984]/85 font-medium leading-relaxed max-w-md mx-auto">
      {body}
    </p>
    <Link
      to="/"
      data-testid="my-lesson-back-home"
      className="inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:underline"
    >
      Back to the homepage <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

const LessonView = ({ data }) => {
  const l = data.lesson;
  return (
    <article
      data-testid="my-lesson-card"
      className="ae-card rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden"
    >
      <header className="bg-[#1c2d3d] text-white px-6 py-6 sm:px-10 sm:py-8">
        <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
          Your free lesson
        </p>
        <h1
          data-testid="my-lesson-title"
          className="mt-2 font-black text-3xl sm:text-4xl tracking-tight leading-tight"
        >
          {l.activity_name}
        </h1>
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge>{data.grade}</Badge>
          <Badge>{data.subject}</Badge>
          <Badge>{data.transferable_skill}</Badge>
          {data.expectation_code && (
            <Badge data-testid="my-lesson-expectation-badge">
              {data.expectation_code}
            </Badge>
          )}
        </div>
      </header>

      <div className="px-6 sm:px-10 py-8 sm:py-10 space-y-9">
        {/* Ontario Curriculum Link — rendered DETERMINISTICALLY from the
            teacher's stored selection (expectation_code + verbatim Ministry
            text), never from model output. Omitted entirely when no
            expectation was chosen. */}
        {data.expectation_code && data.expectation_text && (
          <Section
            icon={<ClipboardList className="h-3.5 w-3.5" />}
            eyebrow="Ontario Curriculum Link"
          >
            <div
              data-testid="my-lesson-expectation"
              className="rounded-md border border-[#4a6984]/15 bg-[#f4f5f7]/60 px-4 py-3"
            >
              <p className="font-extrabold text-[#1c2d3d] text-sm">
                {data.expectation_code}
              </p>
              <p className="mt-1 text-[14px] text-[#4a6984] leading-relaxed font-medium">
                {data.expectation_text}
              </p>
            </div>
          </Section>
        )}

        <Section
          icon={<Target className="h-3.5 w-3.5" />}
          eyebrow="WALT — We Are Learning To"
        >
          <p
            data-testid="my-lesson-walt"
            className="text-lg font-extrabold text-[#1c2d3d] leading-snug"
          >
            {l.walt}
          </p>
        </Section>

        <Section
          icon={<Sparkles className="h-3.5 w-3.5" />}
          eyebrow="Success Criteria"
        >
          <ul className="space-y-2" data-testid="my-lesson-success-criteria">
            {(l.success_criteria || []).map((sc, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-md border border-[#4a6984]/10 bg-[#f4f5f7]/60 px-4 py-3"
              >
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#96ded1] text-[#1c2d3d] text-[10px] font-black">
                  {i + 1}
                </span>
                <span className="text-[15px] font-medium text-[#1c2d3d] leading-snug">
                  {sc}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          icon={<BookOpen className="h-3.5 w-3.5" />}
          eyebrow="Lesson Overview"
        >
          <blockquote
            data-testid="my-lesson-overview"
            className="border-l-4 border-[#96ded1] pl-4 italic text-[15px] text-[#4a6984] leading-relaxed"
          >
            {l.lesson_overview}
          </blockquote>
        </Section>

        <Section eyebrow="Teacher Moves + What to Listen For">
          <div className="overflow-hidden rounded-lg border border-[#4a6984]/15">
            <table className="w-full text-sm" data-testid="my-lesson-teacher-moves">
              <thead>
                <tr className="bg-[#f4f5f7] text-[#1c2d3d] text-[11px] font-extrabold uppercase tracking-[0.14em]">
                  <th className="text-left px-4 py-3 w-1/2">What You Do</th>
                  <th className="text-left px-4 py-3 w-1/2 border-l border-[#4a6984]/15">
                    What to Listen / Look For
                  </th>
                </tr>
              </thead>
              <tbody>
                {(l.teacher_moves || []).map((row, i) => (
                  <tr
                    key={i}
                    className={`text-[14px] text-[#4a6984] leading-relaxed ${
                      i % 2 === 0 ? "bg-white" : "bg-[#f4f5f7]/40"
                    }`}
                  >
                    <td className="align-top px-4 py-4">{row.do}</td>
                    <td className="align-top px-4 py-4 border-l border-[#4a6984]/15 font-medium">
                      {row.listen_for}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section eyebrow="Career Connection">
          <p
            data-testid="my-lesson-career"
            className="text-[15px] text-[#4a6984] leading-relaxed font-medium"
          >
            {l.career_connection}
          </p>
        </Section>

        {/* Phase 142 — Worksheet (rendered only when the teacher
            ticked the Include Worksheet checkbox AND Claude
            populated the section). */}
        {l.worksheet && (
          <Section
            icon={<FileText className="h-3.5 w-3.5" />}
            eyebrow="Student Worksheet"
          >
            <div
              data-testid="my-lesson-worksheet"
              className="rounded-md border border-[#4a6984]/15 bg-white px-4 py-4 sm:px-5"
            >
              {l.worksheet.title && (
                <p className="font-extrabold text-[#1c2d3d] text-base leading-tight">
                  {l.worksheet.title}
                </p>
              )}
              {l.worksheet.instructions && (
                <p className="mt-1.5 text-[14px] text-[#4a6984] italic leading-relaxed">
                  {l.worksheet.instructions}
                </p>
              )}
              {Array.isArray(l.worksheet.items) && l.worksheet.items.length > 0 && (
                <ol className="mt-3 space-y-2 list-decimal pl-5 text-[14px] text-[#1c2d3d] font-medium leading-relaxed">
                  {l.worksheet.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ol>
              )}
            </div>
          </Section>
        )}

        {/* Phase 142 — Exit Ticket. */}
        {l.exit_ticket && (
          <Section
            icon={<ClipboardList className="h-3.5 w-3.5" />}
            eyebrow="Exit Ticket"
          >
            <div
              data-testid="my-lesson-exit-ticket"
              className="rounded-md border-l-4 border-[#96ded1] bg-[#96ded1]/10 px-4 py-3"
            >
              {l.exit_ticket.prompt && (
                <p className="text-[14px] italic text-[#1c2d3d] font-semibold leading-relaxed">
                  {l.exit_ticket.prompt}
                </p>
              )}
              {Array.isArray(l.exit_ticket.questions) &&
                l.exit_ticket.questions.length > 0 && (
                  <ul className="mt-2 space-y-1.5 list-disc pl-5 text-[14px] text-[#1c2d3d] font-medium leading-relaxed">
                    {l.exit_ticket.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ul>
                )}
            </div>
          </Section>
        )}
      </div>
    </article>
  );
};

const Section = ({ eyebrow, icon, children }) => (
  <section>
    <p className="inline-flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#4a6984]/75 mb-3">
      {icon}
      {eyebrow}
    </p>
    {children}
  </section>
);

const Badge = ({ children }) => (
  <span className="rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#96ded1]">
    {children}
  </span>
);

const UpgradeBar = ({ status }) => {
  // Measured-spacer pattern (mirrors MobileStickyCta, Aug 8 2026): render an
  // in-flow sibling spacer sized to the bar's real rendered height so the last
  // bit of page content never hides behind the fixed bar. Replaces the old
  // static `pb-32` (128px) on the root — the bar is `flex-col` on mobile
  // (title + 2-line copy stacked above the CTA) and can exceed 128px on very
  // narrow phones. `offsetHeight` already bakes in py padding.
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(128);
  useEffect(() => {
    const el = barRef.current;
    if (!el) {
      setBarHeight(0);
      return undefined;
    }
    const measure = () => setBarHeight(el.offsetHeight || 128);
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // `status` is the loading-equivalent dep (mirrors MobileStickyCta's
    // `loading`): re-measure when the page transitions loading→ready in case
    // the bar mounts or reflows at that point, so the spacer never stays 0.
  }, [status]);

  return (
    <>
      {/* In-flow spacer sized to the bar; replaces the old static pb-32. */}
      <div
        aria-hidden="true"
        style={{ height: `${barHeight}px` }}
        data-testid="my-lesson-upgrade-spacer"
      />
      <div
        ref={barRef}
        data-testid="my-lesson-upgrade-bar"
        className="fixed inset-x-0 bottom-0 z-40 bg-[#1c2d3d] text-white shadow-2xl border-t border-[#96ded1]/20"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="font-extrabold text-sm sm:text-base tracking-tight leading-tight">
              Want the full version?
            </p>
            <p className="text-[12px] sm:text-[13px] text-white/75 leading-snug">
              Unlock exit tickets, differentiation, slide decks, report card
              language, and unlimited planning.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="my-lesson-upgrade-cta"
            onClick={() => track("free_lesson_upgrade_clicked", { source: "my_lesson_upgrade_bar" })}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-md bg-[#96ded1] text-[#1c2d3d] font-extrabold px-5 py-3 text-sm hover:bg-[#7ecfc0] transition-colors"
          >
            Start free trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </>
  );
};
